import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { LineItem } from 'shared/types/cart';
import { PersonalizeOptions } from 'shared/types/cart/Cart';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import Loader from '@/components/customercenter-ui/common/loader/loader';
import { useAddToCart } from '@/context/addToCartProvider';
import CloseIcon from '@/customercenter-ui/components/icons/close2';
import Minus from '@/customercenter-ui/components/icons/minusCircle';
import Add from '@/customercenter-ui/components/icons/plusCircle';
import { Button } from '@/customercenter-ui/components/ui/button';
import { useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedSite } from '@/helpers/utils/siteUtils';
import { RECENTLY_REMOVED, ORDER_TYPES } from '../../../../../../../frontend/constants/constant';
export interface Props {
  lineItem: LineItem;
  setIsError: (value: boolean) => void;
  setNotificationMessage: (value: string) => void;
  hideButtons: boolean;
  setMaxQuantityWarningMessage: (message: string) => void;
  backOrderValue?: number;
  selectedWarranty?: LineItem;
  count?: any;
  setCount?: any;
  availabilityMsg?: string;
  stockValue?: number;
}

const CartCounter: React.FC<Props> = ({
  lineItem,
  setIsError,
  setNotificationMessage,
  hideButtons,
  setMaxQuantityWarningMessage,
  backOrderValue,
  selectedWarranty,
  count,
  setCount,
  availabilityMsg,
  stockValue,
}) => {
  const { setItemUpdated } = useAddToCart();
  const { removeItem, updateItem, data: cartData } = useCart(false);
  const tooltipContentRef = useRef<HTMLDivElement>(null);
  const projectSettings: ProjectSettings = useProjSettings();
  const [maxQuantityProcessing, setMaxQuantityProcessing] = useState(false);
  const [minQuantityProcessing, setMinQuantityProcessing] = useState(false);
  const [inputQuantityProcessing, setInputQuantityProcessing] = useState(false);
  const [isStockValueLoading, setIsStockValueLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const brandSiteId = getSelectedSite() ?? 'ZBSite';
  const isOnStock = lineItem?.variant?.isOnStock;
  const stockQuantity = stockValue;

  useEffect(() => {
    if (stockValue || backOrderValue) {
      setIsStockValueLoading(false);
    }
  }, [stockValue, backOrderValue]);

  const availableQuantity =
    stockQuantity !== undefined && backOrderValue && backOrderValue > 0
      ? stockQuantity + backOrderValue
      : stockQuantity ?? 0;

  const handleClose = (e: React.MouseEvent) => {
    setOpen(false);
    e.stopPropagation();
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen((prevOpen) => !prevOpen);
    e.stopPropagation();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (tooltipContentRef.current && !tooltipContentRef.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    setCount(lineItem?.count ?? 0);
  }, [lineItem, availableQuantity]);

  useEffect(() => {
    if (availableQuantity === 0) {
      setMaxQuantityProcessing(true);
      setMinQuantityProcessing(true);
    } else {
      setMaxQuantityProcessing(false);
      setMinQuantityProcessing(false);
    }

    if (availableQuantity && projectSettings?.maxItemQuantity) {
      if (availableQuantity >= projectSettings?.maxItemQuantity) {
        if (count >= projectSettings?.maxItemQuantity) {
          setCount(projectSettings?.maxItemQuantity);
          setMaxQuantityProcessing(true);
        } else {
          setMaxQuantityProcessing(false);
        }
        setMaxQuantityWarningMessage('');
      } else {
        if (count >= availableQuantity) {
          setMaxQuantityProcessing(true);
        } else {
          setMaxQuantityProcessing(false);
        }
      }
    }
  }, [
    isOnStock,
    count,
    availableQuantity,
    maxQuantityProcessing,
    minQuantityProcessing,
    setMaxQuantityWarningMessage,
    setMinQuantityProcessing,
    setMaxQuantityProcessing,
  ]);

  const updateCartItem = useCallback(
    async (newQuantity: number) => {
      setLoading(true);
      setMaxQuantityProcessing(true);
      setMinQuantityProcessing(true);
      setInputQuantityProcessing(true);
      if (newQuantity < 1) {
        try {
          if (selectedWarranty) {
            await removeItem(lineItem.lineItemId as string, selectedWarranty?.lineItemId as string);
          } else {
            await removeItem(lineItem.lineItemId as string);
          }
          let personalizeOptions: PersonalizeOptions = {};
          if (lineItem?.variant?.attributes?.personalizeOption && lineItem?.personalizeText) {
            personalizeOptions = {
              personalizeText: lineItem?.personalizeText,
              personalizeProductId: lineItem?.personalizeProductId,
              personalizeUserId: lineItem?.personalizeUserId,
            };
          }
          let warrantyOptions;
          if (selectedWarranty) {
            warrantyOptions = {
              isWarrantySelected: true,
              sku: selectedWarranty?.variant?.sku as string,
            };
          }
          localStorage.setItem(
            RECENTLY_REMOVED,
            JSON.stringify({
              sku: lineItem?.variant?.sku,
              count: lineItem?.count,
              name: lineItem?.name,
              personalizeOption: personalizeOptions,
              warrantyOptions: warrantyOptions,
            }),
          );
          setNotificationMessage(
            `<a href="${lineItem?._url || '#'}"class="underline font-bold">${lineItem?.name}</a> ${
              mapLocaleToMeaningfulFormat([`${brandSiteId}`]).successfullyRemovedFromCart
            }`,
          );

          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
          setIsError(false);
        } catch (error) {
          setNotificationMessage(
            `${mapLocaleToMeaningfulFormat().errorInRemoving} ${lineItem?.name} ${
              mapLocaleToMeaningfulFormat([`${brandSiteId}`]).itemFromCart
            }`,
          );
          setIsError(true);
        }
      } else {
        try {
          if (selectedWarranty) {
            const warrantyOptions = {
              lineItemId: selectedWarranty?.lineItemId,
              count: newQuantity,
            };
            await updateItem(lineItem.lineItemId as string, newQuantity, lineItem?.count ?? 1, warrantyOptions);
          } else {
            await updateItem(lineItem.lineItemId as string, newQuantity, lineItem?.count ?? 1);
          }
          setCount(newQuantity);
        } catch (error) {
          setNotificationMessage(`${mapLocaleToMeaningfulFormat().updateError} ${lineItem?.name}`);
          setIsError(true);
        }
      }
      setItemUpdated(true);
      setMaxQuantityProcessing(false);
      setMinQuantityProcessing(false);
      setInputQuantityProcessing(false);
      setLoading(false);
    },
    [updateItem, removeItem, maxQuantityProcessing, minQuantityProcessing, lineItem],
  );

  useEffect(() => {
    if (
      availableQuantity &&
      availabilityMsg &&
      count >= availableQuantity &&
      availabilityMsg === mapLocaleToMeaningfulFormat().quantityExceededMsg
    ) {
      updateCartItem(availableQuantity);
    }
  }, [availableQuantity, availabilityMsg]);

  return (
    <div className="flex items-center gap-1.5 sm:gap-3.5">
      {loading && (
        <section className="top-[calc(50%_-_15px] z-2 size-[30px] absolute left-[calc(50%_-_15px)]">
          <Loader />
        </section>
      )}
      <Button
        size="icon"
        onClick={async () => {
          await updateCartItem(count - 1);
          setMaxQuantityWarningMessage('');
        }}
        disabled={
          (isStockValueLoading || minQuantityProcessing || inputQuantityProcessing) &&
          cartData?.orderType != ORDER_TYPES.MAIL_ORDER
        }
        className={`size-8 hover:bg-hoverSecondaryButton flex items-center justify-center rounded-full bg-silverGray transition-colors ${
          hideButtons ? 'hidden' : ''
        }`}
        aria-label="Decrease Quantity"
      >
        <Minus />
      </Button>
      {hideButtons ? (
        <Tooltip.Provider>
          <Tooltip.Root open={open}>
            <Tooltip.Trigger onClick={handleTriggerClick} aria-label="tooltip" type="button">
              <input
                autoCorrect="off"
                autoComplete="Number"
                inputMode="numeric"
                type="number"
                value={count === 0 ? '' : count}
                readOnly={true}
                className="numberInputCounter flex h-11 w-11 appearance-none items-center justify-center rounded-sm border border-silverGray text-center"
                aria-labelledby="Quantity Number Input"
              />
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                ref={tooltipContentRef}
                className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade
                relative z-50 mr-2 select-none rounded-[4px] border border-silverGray bg-white px-3 pb-3 pt-2 text-sm leading-4 text-black
                drop-shadow-lg will-change-[transform,opacity]"
                sideOffset={5}
              >
                <section onClick={handleClose} className="pb-4">
                  <CloseIcon className="absolute right-2 top-2 cursor-pointer fill-silverGray" />
                </section>
                {/* <section className="tooltip-scrollbar custom-scrollbar max-h-[246px] max-w-[314px] overflow-auto pr-3">
                  <FreeGiftQtyTooltip />
                </section> */}
                <Tooltip.Arrow className="fill-white drop-shadow-lg" height={20} width={20} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        <input
          autoCorrect="off"
          autoComplete="Number"
          inputMode="numeric"
          type="number"
          value={count === 0 ? '' : count}
          disabled={
            (isStockValueLoading || inputQuantityProcessing || hideButtons) &&
            cartData?.orderType != ORDER_TYPES.MAIL_ORDER
          }
          max={projectSettings?.maxItemQuantity}
          min={0}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d{0,2}$/.test(value)) {
              setCount(Number(value));
            }
            if (Number(value) > availableQuantity) {
              setMaxQuantityWarningMessage(
                `${mapLocaleToMeaningfulFormat([availableQuantity?.toString()]).onlyCanBeOrdered}`,
              );
            } else {
              setMaxQuantityWarningMessage('');
            }
            const quantityToAdd = parseInt(value) <= availableQuantity ? parseInt(value) : availableQuantity;
            setCount(value !== '' ? quantityToAdd : value);
          }}
          onBlur={async () => {
            await updateCartItem(count !== '' ? parseInt(count) : count);
          }}
          className="numberInputCounter gbunmask flex h-11 w-11 appearance-none items-center justify-center rounded-sm border border-silverGray text-center"
          aria-label="Quantity Number Input"
          onFocus={(e: any) => {
            e?.target?.select();
          }}
        />
      )}
      <section
        onClick={() => {
          if (maxQuantityProcessing && !isStockValueLoading) {
            setMaxQuantityWarningMessage(
              `${mapLocaleToMeaningfulFormat([availableQuantity?.toString()]).onlyCanBeOrdered}`,
            );
          }
        }}
      >
        <Button
          size="icon"
          onClick={async () => {
            await updateCartItem(count + 1);
          }}
          disabled={
            (isStockValueLoading || maxQuantityProcessing || inputQuantityProcessing) &&
            cartData?.orderType != ORDER_TYPES.MAIL_ORDER
          }
          className={`size-8 hover:bg-hoverSecondaryButton flex items-center justify-center rounded-full bg-silverGray transition-colors ${
            hideButtons ? 'hidden' : ''
          }`}
          aria-label="Increase Quantity"
        >
          <Add />
        </Button>
      </section>
    </div>
  );
};

export default CartCounter;
